import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import moment from "moment";
import DataTableComponent from "../../common/DataTableComponent";
import { GetOhsBuildingCheckHistory } from "../../../services/FirstAidBoxService";
import { useOhsBuildingCheckFilters, useOhsBuildingCheckHistory } from "../../../query/hooks/QueryHooksFirstAidBox";
import { BUILDING_CHECK_ANSWER_LABELS, BUILDING_CHECK_ANSWERS, REPORT_DATE_TIME_FORMAT } from "./ohsConstants";
import { ConditionBadge, MutedText } from "./Ohs.styles";
import { exportOhsReport, getPrefixedUser, numberChecklistQuestions } from "./ohsUtils";
import OhsUserCell from "./OhsUserCell";

const EMPTY_FILTER = { arr: [], checkObj: {} };
// Search-style filters keep their ticked options in an array, not an object.
const EMPTY_SEARCH_FILTER = { arr: [], checkObj: [] };
const BUILDING_CHECK_EXPORT_COLUMNS = [
    { label: "Date", width: 12 },
    { label: "Time", width: 7 },
    { label: "Campus", width: 16 },
    { label: "Building", width: 22 },
    { label: "Checked By", width: 20 },
    { label: "Result", width: 12 },
    { label: "Yes", width: 6 },
    { label: "No", width: 6 },
    { label: "N/A", width: 6 },
    { label: "General Comments", width: 30 },
];

/**
 * A building check's result in words, e.g. "2 issues" or "All clear".
 * @param {Object} buildingCheck Building Check History row.
 * @returns {string}
 */
function describeResult(buildingCheck) {
    if (buildingCheck.no_count > 0) {
        return `${buildingCheck.no_count} ${buildingCheck.no_count === 1 ? "issue" : "issues"}`;
    }
    return "All clear";
}

/**
 * Building Check History - every monthly building check, newest first: which building, who checked it and
 * when, and every question answered No with its comment. Defaults to the last 6 months; the export
 * carries every answer.
 */
function BuildingCheckHistory() {
    const [tableState, setTableState] = useState({ page: 1, perPage: 10, sortKey: "completed_at", sortOrder: "desc" });
    const [search, setSearch] = useState("");
    const [campusFilter, setCampusFilter] = useState(EMPTY_FILTER);
    const [buildingFilter, setBuildingFilter] = useState(EMPTY_SEARCH_FILTER);
    const [checkedByFilter, setCheckedByFilter] = useState(EMPTY_SEARCH_FILTER);
    const [resultFilter, setResultFilter] = useState(EMPTY_FILTER);
    const [dateRange, setDateRange] = useState({ startDate: moment().subtract(6, "months").toDate(), endDate: new Date() });
    const { data: buildingCheckFilters } = useOhsBuildingCheckFilters();

    const reportParams = useMemo(() => ({
        page: tableState.page,
        limit: tableState.perPage,
        key: tableState.sortKey,
        sort: tableState.sortOrder,
        search,
        viaCampus: campusFilter.arr,
        viaBuilding: buildingFilter.arr,
        viaCheckedBy: checkedByFilter.arr,
        viaResult: resultFilter.arr,
        startDate: dateRange?.startDate ? moment(dateRange.startDate).format("YYYY-MM-DD") : undefined,
        endDate: dateRange?.endDate ? moment(dateRange.endDate).format("YYYY-MM-DD") : undefined,
    }), [tableState, search, campusFilter, buildingFilter, checkedByFilter, resultFilter, dateRange]);
    const { data: historyPage, isFetching } = useOhsBuildingCheckHistory(reportParams);

    // A new filter or search starts from page 1, or the table can land on a page that no longer exists.
    useEffect(() => {
        setTableState((currentState) => ({ ...currentState, page: 1 }));
    }, [search, campusFilter, buildingFilter, checkedByFilter, resultFilter, dateRange]);

    const columns = useMemo(
        () => [
            {
                name: "Date",
                selector: "completed_at",
                sortField: "completed_at",
                sortable: true,
                minWidth: "150px",
                cell: (row) => moment(row.completed_at).format(REPORT_DATE_TIME_FORMAT),
            },
            {
                name: "Building",
                selector: "building_name",
                sortField: "building_name",
                sortable: true,
                minWidth: "180px",
                cell: (row) => (
                    <div>
                        <div>{row.building_name || "-"}</div>
                        <MutedText>{row.campus_name}</MutedText>
                    </div>
                ),
            },
            {
                name: "Checked By",
                selector: "checked_by_name",
                sortField: "checked_by_name",
                sortable: true,
                minWidth: "190px",
                cell: (row) => <OhsUserCell user={getPrefixedUser(row, "checked_by", row.checked_by_id)} />,
            },
            {
                name: "Result",
                selector: "no_count",
                sortField: "no_count",
                sortable: true,
                minWidth: "170px",
                cell: (row) => (
                    <div>
                        <ConditionBadge $condition={row.no_count > 0 ? "damaged" : "ok"}>{describeResult(row)}</ConditionBadge>
                        <div><MutedText>{row.yes_count} Yes · {row.no_count} No · {row.na_count} N/A</MutedText></div>
                    </div>
                ),
            },
            {
                name: "Issues Found",
                minWidth: "280px",
                grow: 3,
                cell: (row) => {
                    const issueAnswers = numberChecklistQuestions(row.answers).filter((checkAnswer) => checkAnswer.answer === BUILDING_CHECK_ANSWERS.no);
                    if (!issueAnswers.length) {
                        return <MutedText>None</MutedText>;
                    }
                    return (
                        <div>
                            {issueAnswers.map((checkAnswer) => (
                                <div key={checkAnswer.question_id}>
                                    {checkAnswer.number}. {checkAnswer.question} <MutedText>- {checkAnswer.comment}</MutedText>
                                </div>
                            ))}
                        </div>
                    );
                },
            },
            {
                name: "General Comments",
                minWidth: "200px",
                grow: 2,
                cell: (row) => (row.notes ? <span title={row.notes}>{row.notes}</span> : <MutedText>-</MutedText>),
            },
        ],
        []
    );

    /**
     * Exports every building check matching the current filters (not just this page). One row per check,
     * then an Answer / Comment column pair per question, in checklist order.
     * @param {string} fileType xlsx | csv | pdf
     * @param {string} fileName Download name.
     */
    const handleExport = async (fileType, fileName) => {
        try {
            const { data } = await GetOhsBuildingCheckHistory({ ...reportParams, exportStatus: "true" });
            const buildingChecks = data?.data ?? [];

            // Every question asked on any exported check - one added or removed between checks still gets its own columns.
            const exportQuestionsById = new Map();
            buildingChecks.forEach((buildingCheck) => buildingCheck.answers.forEach((checkAnswer) => exportQuestionsById.set(checkAnswer.question_id, checkAnswer)));
            const exportQuestions = numberChecklistQuestions([...exportQuestionsById.values()]);

            await exportOhsReport({
                fileType,
                fileName,
                frozenColumns: 5,
                columns: [
                    ...BUILDING_CHECK_EXPORT_COLUMNS,
                    ...exportQuestions.flatMap((exportQuestion) => {
                        const questionGroup = `${exportQuestion.section} - ${exportQuestion.number}. ${exportQuestion.question}`;
                        return [
                            { group: questionGroup, label: "Answer", width: 9 },
                            { group: questionGroup, label: "Comment", width: 28 },
                        ];
                    }),
                ],
                rows: buildingChecks.map((buildingCheck) => {
                    const answerByQuestion = new Map(buildingCheck.answers.map((checkAnswer) => [checkAnswer.question_id, checkAnswer]));
                    return [
                        moment(buildingCheck.completed_at).format("YYYY-MM-DD"),
                        moment(buildingCheck.completed_at).format("HH:mm"),
                        buildingCheck.campus_name,
                        buildingCheck.building_name,
                        buildingCheck.checked_by_name,
                        { value: describeResult(buildingCheck), tone: buildingCheck.no_count > 0 ? "damaged" : "ok" },
                        buildingCheck.yes_count,
                        buildingCheck.no_count,
                        buildingCheck.na_count,
                        buildingCheck.notes || "",
                        ...exportQuestions.flatMap((exportQuestion) => {
                            const checkAnswer = answerByQuestion.get(exportQuestion.question_id);
                            // A question that wasn't on the checklist when this check was done.
                            if (!checkAnswer) {
                                return ["", ""];
                            }
                            return [
                                // Only a No is coloured - colour on every Yes would drown the issues out.
                                { value: BUILDING_CHECK_ANSWER_LABELS[checkAnswer.answer], tone: checkAnswer.answer === BUILDING_CHECK_ANSWERS.no ? "damaged" : null },
                                checkAnswer.comment || "",
                            ];
                        }),
                    ];
                }),
                pdfSections: buildingChecks.map((buildingCheck) => ({
                    title: `${moment(buildingCheck.completed_at).format(REPORT_DATE_TIME_FORMAT)} - Building check of ${buildingCheck.building_name} (${buildingCheck.campus_name})`,
                    summary: `Checked by ${buildingCheck.checked_by_name} · ${describeResult(buildingCheck)} (${buildingCheck.yes_count} Yes, ${buildingCheck.no_count} No, ${buildingCheck.na_count} N/A)${buildingCheck.notes ? ` · ${buildingCheck.notes}` : ""}`,
                    header: ["No.", "Section", "Question", "Answer", "Comment"],
                    rows: numberChecklistQuestions(buildingCheck.answers).map((checkAnswer) => [
                        checkAnswer.number,
                        checkAnswer.section,
                        checkAnswer.question,
                        BUILDING_CHECK_ANSWER_LABELS[checkAnswer.answer],
                        checkAnswer.comment || "",
                    ]),
                })),
            });
        } catch (error) {
            console.log("BuildingCheckHistory.js->handleExport->Error", error);
            Swal.fire("Error", "The building check history could not be exported.", "error");
        }
    };

    return (
        <DataTableComponent
            data={historyPage?.data ?? []}
            loading={isFetching}
            state={tableState}
            setState={setTableState}
            setSearch={setSearch}
            totalRows={historyPage?.total ?? 0}
            columns={columns}
            exportFunction={handleExport}
            exportFileName="OHS_Building_Check_History"
            filters={[
                {
                    filterName: "Date",
                    state: dateRange,
                    setState: setDateRange,
                    dateRangeFilter: true,
                },
                {
                    filterName: "Campus",
                    optionArr: buildingCheckFilters.CampusFilter || [],
                    state: campusFilter,
                    setState: setCampusFilter,
                    uniqueId: "ohsBuildingHistoryCampus",
                },
                {
                    filterName: "Building",
                    optionArr: buildingCheckFilters.BuildingFilter || [],
                    state: buildingFilter,
                    setState: setBuildingFilter,
                    uniqueId: "ohsBuildingHistoryBuilding",
                    isSearchFilter: true,
                },
                {
                    filterName: "Checked By",
                    optionArr: buildingCheckFilters.CheckedByFilter || [],
                    state: checkedByFilter,
                    setState: setCheckedByFilter,
                    uniqueId: "ohsBuildingHistoryCheckedBy",
                    isSearchFilter: true,
                },
                {
                    filterName: "Result",
                    optionArr: buildingCheckFilters.ResultFilter || [],
                    state: resultFilter,
                    setState: setResultFilter,
                    uniqueId: "ohsBuildingHistoryResult",
                },
            ]}
        />
    );
}

export default BuildingCheckHistory;
